import { useMutation, useQueryClient, type QueryClient } from '@tanstack/react-query';
import { useRef } from 'react';
import { hydraUiApi } from '@/services/api';
import type { HookMutationConfig } from '../shared/react-query-types';
import type { UsersUpdateUserSettingsRequest } from '@customerio/hydra-ui-api';
import { restoreUserSettingsCache, setUserSettingsCache, type AuthenticatedQueryData } from './cache';
import {
  parseUserSettingsResponse,
  userSettingsKeys,
  type ThemePreferences,
  type SideNavPreferences,
  type UserSettings,
} from './entity';

export interface UpdateUserSettingsInput {
  accountId: number;
  userId: number;
  sideNavPreferences?: SideNavPreferences;
  themePreferences?: ThemePreferences;
}

export async function updateUserSettings(input: UpdateUserSettingsInput): Promise<UserSettings> {
  // Each provided top-level field fully replaces its blob; omitted fields are left
  // untouched, so a theme-only or side-nav-only update is safe.
  const body: { side_nav_preferences?: SideNavPreferences; theme_preferences?: ThemePreferences } = {};
  if (input.sideNavPreferences != null) {
    body.side_nav_preferences = input.sideNavPreferences;
  }
  if (input.themePreferences != null) {
    body.theme_preferences = input.themePreferences;
  }

  const response = await hydraUiApi.users.updateSettings({
    params: {
      path: {
        account_id: String(input.accountId),
        id: String(input.userId),
      },
    },
    // `theme_preferences` joins the generated request body once the hydra-ui-api client
    // is regenerated against the merged services spec; cast at this boundary until then.
    body:
      // oxlint-disable-next-line typescript/consistent-type-assertions -- pre-existing assertion; replace with narrowing when touched
      body as UsersUpdateUserSettingsRequest,
  });

  return parseUserSettingsResponse(response, {
    accountId: input.accountId,
    userId: input.userId,
  });
}

type UseUpdateUserSettingsVariables = Omit<UpdateUserSettingsInput, 'accountId' | 'userId'>;

type UpdateUserSettingsMutationContext = {
  previousAuthenticatedQueryData: AuthenticatedQueryData | undefined;
  previousUserSettings: UserSettings | undefined;
  mutationContext: unknown;
};

type CachedUserSettingsSnapshot = Omit<UpdateUserSettingsMutationContext, 'mutationContext'>;

function getSideNavPreferencesFromSnapshot(snapshot: CachedUserSettingsSnapshot | undefined) {
  return (
    snapshot?.previousUserSettings?.side_nav_preferences ??
    snapshot?.previousAuthenticatedQueryData?.user?.settings?.side_nav_preferences
  );
}

function getConfirmedSnapshot(
  qc: QueryClient,
  settings: UserSettings,
  fallbackSideNavPreferences?: SideNavPreferences,
): CachedUserSettingsSnapshot {
  const sideNavPreferences = settings.side_nav_preferences ?? fallbackSideNavPreferences;
  const authenticatedQueryData = qc.getQueryData<AuthenticatedQueryData>(['authenticated']);

  return {
    previousUserSettings: settings,
    previousAuthenticatedQueryData:
      authenticatedQueryData?.user == null || sideNavPreferences == null
        ? authenticatedQueryData
        : {
            ...authenticatedQueryData,
            user: {
              ...authenticatedQueryData.user,
              settings: {
                ...authenticatedQueryData.user.settings,
                side_nav_preferences: sideNavPreferences,
              },
            },
          },
  };
}

interface UseUpdateUserSettingsOptions {
  accountId: number | null | undefined;
  userId: number | null | undefined;
  mutationConfig?: HookMutationConfig<UserSettings, UseUpdateUserSettingsVariables>;
}

export const useUpdateUserSettings = ({ accountId, userId, mutationConfig }: UseUpdateUserSettingsOptions) => {
  const qc = useQueryClient();
  const latestSideNavPreferencesRef = useRef<SideNavPreferences | undefined>(undefined);
  const confirmedSnapshotRef = useRef<CachedUserSettingsSnapshot | undefined>(undefined);
  const pendingMutationCountRef = useRef(0);
  const { onError, onMutate, onSettled, onSuccess, ...rest } = mutationConfig ?? {};

  return useMutation({
    ...rest,
    scope: accountId != null && userId != null ? { id: `user-settings:${accountId}:${userId}` } : undefined,
    mutationFn: (variables: UseUpdateUserSettingsVariables) => {
      if (accountId == null || userId == null) {
        throw new Error('Cannot update user settings without an authenticated user');
      }

      return updateUserSettings({ accountId, userId, ...variables });
    },
    onMutate: async (variables, context): Promise<UpdateUserSettingsMutationContext> => {
      const previousUserSettings =
        accountId != null && userId != null
          ? qc.getQueryData<UserSettings>(userSettingsKeys.detail(accountId, userId))
          : undefined;
      const previousAuthenticatedQueryData = qc.getQueryData<AuthenticatedQueryData>(['authenticated']);

      latestSideNavPreferencesRef.current = variables.sideNavPreferences;
      pendingMutationCountRef.current += 1;
      confirmedSnapshotRef.current ??= {
        previousAuthenticatedQueryData,
        previousUserSettings,
      };

      if (accountId != null && userId != null) {
        setUserSettingsCache(qc, {
          accountId,
          userId,
          side_nav_preferences: variables.sideNavPreferences,
        });
      }

      return {
        previousAuthenticatedQueryData,
        previousUserSettings,
        mutationContext: await onMutate?.(variables, context),
      };
    },
    onError: (error, variables, context, mutation) => {
      if (latestSideNavPreferencesRef.current === variables.sideNavPreferences) {
        const snapshot = confirmedSnapshotRef.current ?? context;
        latestSideNavPreferencesRef.current = getSideNavPreferencesFromSnapshot(snapshot);
        restoreUserSettingsCache(
          qc,
          accountId,
          userId,
          snapshot?.previousUserSettings,
          snapshot?.previousAuthenticatedQueryData,
        );
        onError?.(error, variables, context?.mutationContext, mutation);
      }
    },
    onSuccess: (settings, variables, context, mutation) => {
      const nextSettings = {
        ...settings,
        side_nav_preferences: settings.side_nav_preferences ?? variables.sideNavPreferences,
      };
      confirmedSnapshotRef.current = getConfirmedSnapshot(qc, nextSettings, variables.sideNavPreferences);

      if (latestSideNavPreferencesRef.current === variables.sideNavPreferences) {
        latestSideNavPreferencesRef.current = nextSettings.side_nav_preferences;
        setUserSettingsCache(qc, nextSettings, variables.sideNavPreferences);
      }

      onSuccess?.(nextSettings, variables, context?.mutationContext, mutation);
    },
    onSettled: (settings, error, variables, context, mutation) => {
      pendingMutationCountRef.current = Math.max(0, pendingMutationCountRef.current - 1);

      if (pendingMutationCountRef.current === 0) {
        confirmedSnapshotRef.current = undefined;
      }

      onSettled?.(settings, error, variables, context?.mutationContext, mutation);
    },
  });
};
